// Operating Expenses (requirement 18). Manual entries are stored here;
// petty cash and payroll are NEVER copied in — they're merged in at read
// time (getOperatingExpensesForPeriod) from their own collections, same
// "don't duplicate what's derivable" principle as Stage 2's GST control.
import {
  collection, doc, getDocs, query, runTransaction, serverTimestamp, where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { sumPayrollForPeriod } from "./payrollService";
import { assertPeriodOpen } from "./periodLockService";

const EXPENSES = "operating_expenses";
const PETTY_CASH = "petty_cash_expenses";

export const EXPENSE_CATEGORIES = Object.freeze([
  "PERMANENT_ALM_PAYROLL", "OFFICE_RENT", "UTILITIES", "FUEL_AND_TRANSPORT", "PETTY_CASH",
  "DIRECTOR_EXPENSES", "DONATIONS", "PROFESSIONAL_FEES", "BANK_CHARGES", "SOFTWARE_SUBSCRIPTIONS",
  "REPAIRS_AND_MAINTENANCE", "MARKETING", "TRAVEL", "OFFICE_SUPPLIES", "OTHER_ADMINISTRATIVE",
  "PAYMENT_TRANSFER_ON_DIRECTOR_INSTRUCTIONS", "MEAL_AND_ENTERTAINMENT_EXPENSE", "BORROWING_COST_EXPENSE",
  "CREDIT_CARD_EXPENSE", "LOAN_TO_DIRECTOR", "ASSETS", "PRINTING_AND_STATIONARY", "EDUCATIONAL_EXPENSE",
  "PENALTY_SOCIAL_SECURITY", "PENALTY_EOBI", "OTHER_PENALTY"
]);

export async function createExpense(input) {
  const category = String(input.category || "").trim();
  if (!EXPENSE_CATEGORIES.includes(category)) throw new Error("Select a valid expense category.");
  if (!input.accountingPeriod) throw new Error("Enter the accounting period (YYYY-MM).");
  const grossAmount = Math.round(Number(input.grossAmount || 0));
  if (!(grossAmount > 0)) throw new Error("Enter an amount greater than zero.");

  await assertPeriodOpen(input.accountingPeriod);
  const expenseRef = doc(collection(db, EXPENSES));
  await runTransaction(db, async (transaction) => {
    transaction.set(expenseRef, {
      accountingPeriod: input.accountingPeriod,
      expenseDate: input.expenseDate || input.accountingPeriod + "-01",
      category,
      description: String(input.description || "").trim(),
      vendor: String(input.vendor || "").trim(),
      note: String(input.note || "").trim(),
      referenceNumber: String(input.referenceNumber || "").trim(),
      amountBeforeTax: Math.round(Number(input.amountBeforeTax || grossAmount)),
      taxAmount: Math.round(Number(input.taxAmount || 0)),
      grossAmount,
      paymentMethod: String(input.paymentMethod || "").trim(),
      sourceSystem: "MANUAL",
      sourceRecordId: null,
      status: "DRAFT",
      enteredByUid: auth.currentUser?.uid || null,
      enteredByEmail: auth.currentUser?.email || null,
      enteredAt: serverTimestamp()
    });
  });
  return { id: expenseRef.id };
}

export async function approveExpense(expenseId) {
  await runTransaction(db, async (transaction) => {
    const ref = doc(db, EXPENSES, expenseId);
    const snap = await transaction.get(ref);
    if (!snap.exists()) throw new Error("Expense not found.");
    if (snap.data().status === "APPROVED") return;
    await assertPeriodOpen(snap.data().accountingPeriod);
    transaction.update(ref, {
      status: "APPROVED",
      approvedBy: auth.currentUser?.uid || null,
      approvedByEmail: auth.currentUser?.email || null,
      approvedAt: serverTimestamp()
    });
  });
}

// Rejected entries stay in place (delete is blocked entirely, by design —
// see firestore.rules) but are simply excluded from getOperatingExpensesForPeriod's
// approved-only totals, the same way a still-DRAFT entry already is.
export async function rejectExpense(expenseId, reason) {
  await runTransaction(db, async (transaction) => {
    const ref = doc(db, EXPENSES, expenseId);
    const snap = await transaction.get(ref);
    if (!snap.exists()) throw new Error("Expense not found.");
    if (snap.data().status === "REJECTED") return;
    await assertPeriodOpen(snap.data().accountingPeriod);
    transaction.update(ref, {
      status: "REJECTED",
      rejectedBy: auth.currentUser?.uid || null,
      rejectedByEmail: auth.currentUser?.email || null,
      rejectionReason: String(reason || "").trim(),
      rejectedAt: serverTimestamp()
    });
  });
}

export async function listManualExpenses(accountingPeriod) {
  const q = query(collection(db, EXPENSES), where("accountingPeriod", "==", accountingPeriod));
  const result = await getDocs(q);
  return result.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Read-only view of the petty cash recorded for a period — deduped naturally
// since each source doc is only ever read once, never copied. A voucher counts
// in the month of its expense date as soon as it has been recorded; whether it
// has been exported to QuickBooks is only an integration status
// (exportedToQuickBooks) and plays no part in what the P&L shows. A reversed
// expense is a hard delete upstream, so it simply no longer exists to be read.
export async function listPettyCashForPeriod(accountingPeriod) {
  const q = query(
    collection(db, PETTY_CASH),
    where("expenseDate", ">=", `${accountingPeriod}-01`),
    where("expenseDate", "<=", `${accountingPeriod}-31`)
  );
  const result = await getDocs(q);
  return result.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((expense) => String(expense.expenseDate || "").slice(0, 7) === accountingPeriod);
}

// Combines manual (APPROVED only) + petty cash + the period's payroll total
// into one Operating Expenses total for Monthly P&L — nothing here is
// stored; it's assembled fresh every time.
export async function getOperatingExpensesForPeriod(accountingPeriod) {
  const [manual, pettyCash, payrollTotal] = await Promise.all([
    listManualExpenses(accountingPeriod),
    listPettyCashForPeriod(accountingPeriod),
    sumPayrollForPeriod(accountingPeriod)
  ]);
  const approvedManual = manual.filter((e) => e.status === "APPROVED");
  const manualTotal = approvedManual.reduce((sum, e) => sum + Number(e.grossAmount || 0), 0);
  const pettyCashTotal = pettyCash.reduce((sum, e) => sum + Number(e.amount || 0), 0);

  // Category breakdown for the Monthly P&L report — manual expenses by their
  // own category, petty cash and payroll each rolled into one line since
  // they aren't further categorized at the source.
  const byCategory = {};
  approvedManual.forEach((e) => {
    byCategory[e.category] = Math.round((byCategory[e.category] || 0) + Number(e.grossAmount || 0));
  });
  if (pettyCashTotal) byCategory.PETTY_CASH = Math.round(pettyCashTotal);
  if (payrollTotal) byCategory.PERMANENT_ALM_PAYROLL = Math.round(payrollTotal);

  return {
    accountingPeriod,
    manual: approvedManual,
    manualTotal: Math.round(manualTotal),
    pettyCash,
    pettyCashTotal: Math.round(pettyCashTotal),
    payrollTotal,
    byCategory,
    total: Math.round(manualTotal + pettyCashTotal + payrollTotal)
  };
}
